"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Gift,
  Loader2,
  Minus,
  Plus,
  X,
} from "lucide-react";

import { publicApi } from "@/lib/dpop-client";
import { FEXR_WALLET_STORE } from "@/lib/fexr-wallet";
import {
  clearPendingReferralCode,
  getPendingReferral,
  isLinkToken,
  isValidReferralCode,
  parseInviteInput,
  PENDING_REFERRAL_EVENT,
  storePendingReferralCode,
} from "@/lib/pending-referral";
import { cn } from "@/lib/utils";

// ─── ReferralCodeField ────────────────────────────────────────────────────────
// Collapsed "Have an invite link?" tile on login and register. The input is
// the token after a fixed fexr.io/ prefix; it is checked live against the
// public, uncounted GET /v1/journeys/links/{token}/resolve and the referral
// code behind it is stored for PendingReferralApplier. Bare legacy codes that
// resolve to no link are kept as codes and checked by apply-code after sign-up.
// A link-opened visitor arrives with the tile already filled.

type InviteStatus =
  | "idle"
  | "checking"
  | "valid"
  | "code"
  | "not_invite"
  | "not_found";

const CHECK_DELAY_MS = 450;

async function resolveToken(
  token: string,
): Promise<{ state: "referral"; code: string } | { state: "other" } | null> {
  try {
    const res = await publicApi.get(
      `/v1/journeys/links/${encodeURIComponent(token)}/resolve`,
      { cache: "no-store" },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.state !== "ready" && data?.state !== "read_only") return null;
    const code = data?.intent?.code;
    if (data?.kind === "referral" && typeof code === "string") {
      return { state: "referral", code };
    }
    return { state: "other" };
  } catch {
    return null;
  }
}

export function ReferralCodeField({
  mode = "register",
}: {
  mode?: "login" | "register";
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<InviteStatus>("idle");
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mirror a stored invite (captured from a link, ?join=, or an earlier
  // visit), including captures that land after mount.
  useEffect(() => {
    const sync = () => {
      const pending = getPendingReferral();
      if (!pending) return;
      seq.current += 1;
      setValue(pending.token ?? pending.code);
      setStatus(pending.token ? "valid" : "code");
    };
    sync();
    window.addEventListener(PENDING_REFERRAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_REFERRAL_EVENT, sync);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const check = useCallback(async (raw: string) => {
    const run = ++seq.current;
    const parsed = parseInviteInput(raw);
    if (!parsed) {
      setStatus("idle");
      return;
    }

    if (parsed.kind === "code") {
      if (isValidReferralCode(parsed.code)) {
        storePendingReferralCode(parsed.code, null);
        setValue(parsed.code);
        setStatus("code");
      } else {
        setStatus("not_found");
      }
      return;
    }

    setStatus("checking");
    // Generated tokens are lowercase, custom names uppercase: try as typed,
    // then uppercased.
    const candidates = Array.from(
      new Set([parsed.token, parsed.token.toUpperCase()]),
    ).filter(isLinkToken);

    let sawOtherLink = false;
    for (const token of candidates) {
      const result = await resolveToken(token);
      if (run !== seq.current) return;
      if (result?.state === "referral") {
        storePendingReferralCode(result.code, token);
        setValue(token);
        setStatus("valid");
        return;
      }
      if (result?.state === "other") sawOtherLink = true;
    }

    if (sawOtherLink) {
      setStatus("not_invite");
      return;
    }
    // Legacy bare code with no link behind it: apply-code decides after
    // sign-up.
    if (isValidReferralCode(parsed.token)) {
      storePendingReferralCode(parsed.token, null);
      setValue(parsed.token.toUpperCase());
      setStatus("code");
      return;
    }
    setStatus("not_found");
  }, []);

  function handleChange(next: string) {
    const trimmed = next.slice(0, 120);
    setValue(trimmed);
    clearPendingReferralCode();
    seq.current += 1;
    if (timer.current) clearTimeout(timer.current);
    if (!trimmed.trim()) {
      setStatus("idle");
      return;
    }
    setStatus("checking");
    timer.current = setTimeout(() => check(trimmed), CHECK_DELAY_MS);
  }

  function clear() {
    if (timer.current) clearTimeout(timer.current);
    seq.current += 1;
    clearPendingReferralCode();
    setValue("");
    setStatus("idle");
    inputRef.current?.focus();
  }

  function expand() {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 160);
  }

  const accepted = status === "valid" || status === "code";
  const applyNote =
    mode === "login"
      ? "Applies if this sign-in creates your account."
      : "Applied once your account is created.";
  const message: Record<InviteStatus, string> = {
    idle: "Paste the link a friend sent you.",
    checking: "Checking…",
    valid: `Friend's invite found. ${applyNote}`,
    code: `Invite code saved. ${applyNote}`,
    not_invite: "That link isn't an invite.",
    not_found: "Link not found. Check it and try again.",
  };
  const display = status === "code" ? `code ${value}` : `fexr.io/${value}`;

  return (
    <div className="overflow-hidden rounded-lg border border-border/10 bg-card dark:border-white/10 dark:bg-card">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : expand())}
        aria-expanded={open}
        aria-controls="invite-link-panel"
        className="flex w-full items-center gap-2 px-3.5 py-3 text-left text-xs font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      >
        <Gift className="h-3.5 w-3.5 shrink-0 text-primary" />
        {!open && accepted ? (
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="shrink-0 text-muted-foreground">Invited via</span>
            <span className="truncate font-mono">{display}</span>
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success dark:text-success" />
          </span>
        ) : (
          <span className="flex-1">
            {open ? "Invite link" : "Have an invite link?"}
          </span>
        )}
        {!open && accepted ? (
          <span className="shrink-0 text-xs font-normal text-muted-foreground underline underline-offset-2">
            Change
          </span>
        ) : open ? (
          <Minus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id="invite-link-panel"
            key="invite-link-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <div className="px-3.5 pb-3.5">
              <div
                className={cn(
                  "flex h-10 items-center rounded-md border bg-black/[0.035] focus-within:ring-2 focus-within:ring-primary/60 dark:bg-black/20",
                  status === "not_found" || status === "not_invite"
                    ? "border-destructive/60"
                    : "border-border/10 dark:border-white/10",
                )}
              >
                <label
                  htmlFor="invite-link"
                  className="select-none border-r border-border/10 pl-3 pr-2 font-mono text-sm text-muted-foreground dark:border-white/10"
                >
                  fexr.io/
                </label>
                <input
                  ref={inputRef}
                  id="invite-link"
                  value={value}
                  onChange={(event) => handleChange(event.target.value)}
                  placeholder="yourfriend"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={
                    status === "not_found" || status === "not_invite"
                  }
                  aria-describedby="invite-link-message"
                  className="h-full min-w-0 flex-1 bg-transparent px-2 font-mono text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
                />
                <span className="flex w-9 shrink-0 items-center justify-center">
                  {status === "checking" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : accepted ? (
                    <CheckCircle2 className="h-4 w-4 text-success dark:text-success" />
                  ) : value ? (
                    <button
                      type="button"
                      onClick={clear}
                      aria-label="Clear invite link"
                      className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </span>
              </div>
              <p
                id="invite-link-message"
                aria-live="polite"
                className={cn(
                  "mt-1.5 text-xs",
                  status === "not_found" || status === "not_invite"
                    ? "text-destructive"
                    : accepted
                      ? "text-success dark:text-success"
                      : "text-muted-foreground",
                )}
              >
                {message[status]}
                {accepted && (
                  <button
                    type="button"
                    onClick={clear}
                    className="ml-1.5 text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    Remove
                  </button>
                )}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── ReferralJoinActions ──────────────────────────────────────────────────────
// Client actions for app/join/[code] on phones. The page stores the code for
// the web flow and offers the wallet with the code carried across install.

const APP_STORE_URL = FEXR_WALLET_STORE.ios;
const PLAY_STORE_URL = FEXR_WALLET_STORE.android;

export function ReferralJoinActions({
  code,
  platform,
}: {
  code: string;
  platform: "ios" | "android" | "other";
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    storePendingReferralCode(code);
  }, [code]);

  const copyAndOpenStore = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {}
    window.location.href = APP_STORE_URL;
  };

  const buttonBase =
    "flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90";

  return (
    <div className="space-y-2.5">
      <Link
        href="/register"
        className={`${buttonBase} bg-primary text-primary-foreground`}
      >
        Continue on web
        <ArrowRight className="h-4 w-4" />
      </Link>

      {/* Fallback for in-app browsers (Telegram, X, WhatsApp) where
          universal links don't fire. */}
      <a
        href={`fexr://join/${code}`}
        className={`${buttonBase} border border-white/10 bg-white/5 text-foreground`}
      >
        Open in Fexr
      </a>

      {platform === "android" && (
        <a
          href={`${PLAY_STORE_URL}&referrer=${encodeURIComponent(
            `fexr_ref=${code}`,
          )}`}
          className={`${buttonBase} border border-white/10 bg-white/5 text-foreground`}
        >
          Get the Fexr app
        </a>
      )}

      {platform === "ios" && (
        <>
          <button
            type="button"
            onClick={copyAndOpenStore}
            className={`${buttonBase} border border-white/10 bg-white/5 text-foreground`}
          >
            {copied ? (
              <Check className="h-4 w-4 text-success" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            Copy code &amp; get the app
          </button>
          <p className="text-center text-xs text-muted-foreground">
            Paste the code when the app asks for a referral code.
          </p>
        </>
      )}
    </div>
  );
}
